// routeToUrl — a `Route` to its canonical URL string (path + route-owned query
// only). Pure, total, the exact inverse of `parseRoute` on every route.
//
// The returned string carries ONLY the route's own keys (the `/…` path plus
// `q`/`filter` for the library). It deliberately omits ambient query params the
// route layer does not own, so comparing two CANONICAL routeToUrl strings is
// exactly the idempotence guard that keeps a frame→URL push from clobbering the
// bar (or looping). Because this is the one canonical spelling, the guard is
// insensitive to query ordering and to any unowned param.
//
// `notFound` serializes back to its captured pathname — a 404 keeps the URL you
// typed rather than rewriting it.
//
// Discipline (dev-ts): pure — no I/O, no location read; total, ending in
// `exhaustSwitchCase`.

import { exhaustSwitchCase } from "@bilda/utils";
import type { Route } from "./types.js";

// Encode one opaque id for a path segment. Ids are pass-through strings; a
// stray "/" or "%" must survive the round-trip, so encode on the way out and
// decode on the way in (parse.ts).
const seg = (id: string): string => encodeURIComponent(id);

// Build "?a=b&c=d" from present entries only (empty ⇒ ""), URL-encoded. Keeps
// the library's canonical spelling stable (`q` before `filter`) so the
// idempotence comparison in useUrlNavigation is a plain string equality.
const query = (
    entries: readonly (readonly [string, string | undefined])[],
): string => {
    const params = new URLSearchParams();
    for (const [key, value] of entries)
        if (value !== undefined && value !== "") params.set(key, value);
    const s = params.toString();
    return s === "" ? "" : `?${s}`;
};

export const routeToUrl = (route: Route): string => {
    switch (route.type) {
        case "chat":
            return "/";
        case "journal":
            return "/journal";
        case "studio":
            return route.projectId === undefined
                ? "/studio"
                : `/projects/${seg(route.projectId)}`;
        case "classicStudio":
            return route.projectId === undefined
                ? "/classic/studio"
                : `/classic/projects/${seg(route.projectId)}`;
        case "projects":
            return "/projects";
        case "knowledge":
            return `/knowledge${
                route.selected === undefined ? "" : `/${seg(route.selected)}`
            }`;
        case "library":
            return `/library${
                route.selected === undefined ? "" : `/${seg(route.selected)}`
            }${query([
                ["q", route.query],
                ["filter", route.facet],
            ])}`;
        case "conversation":
            return `/c/${seg(route.conversationId)}`;
        case "package":
            return `/package/${seg(route.id)}`;
        case "quality":
            return `/quality/${seg(route.materialId)}`;
        case "settings":
            return `/settings/${seg(route.section)}`;
        case "notFound":
            return route.path;
        default:
            return exhaustSwitchCase(route);
    }
};
